import { Link, List, ListItem, ListItemText } from "@mui/material";
import { format, parseISO } from "date-fns";
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getActiveStatus } from "utils/ecommerseTypes";
import { dateFormats } from "utils/formats";
import { dateIsPastOrToday, prettifyOrgNumber } from "utils/helpers";
import EditableTextField from "./drawers/editableTextField";
import CountryField from "./drawers/membershipInformation/countryField";
import DocumentsField from "./drawers/membershipInformation/documentsField";
import FreeFromTextField from "./drawers/membershipInformation/freeFormTextField";
import InvoiceAddressFields from "./drawers/membershipInformation/invoiceAddresFields";
import InvoiceMethodFields from "./drawers/membershipInformation/invoiceMethodFields";
import OrganizationNameField from "./drawers/membershipInformation/organizationNameField";
import { LocalizeText } from "./localizer";
import { IAlternativeInvoiceRecipient, IMembership } from "reducers/memberships";
import HasPermission from "./__helpers__/hasPermission";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { MembershipsContext } from "contexts/memberships";
import { usePermission } from "utils/hooks/usePermission";
import { IButtonProps, ListItemWithButton } from "./drawers/subscription/listItemWithButton";
import { ModalContext } from "contexts/modals";
import SetAlternativeRecipientDrawer from "./drawers/setAlternativeRecipientDrawer";
import ExistingRecipientDrawer from "./drawers/existingRecipientDrawer";
import { InvoicingInfo } from "./drawers/invoicingInfo";
import { LanguageContext } from "contexts/language";
import ReferencePersonFields from "./drawers/membershipInformation/referencePersonFields";
import EditStatusMembershipDrawer from "./drawers/editStatusMembershipDrawer";
import VatNumberField from "./drawers/membershipInformation/vatNumberFields";
import PoNumberField from "./drawers/membershipInformation/poNumberFields";

interface IProps {
    membership: IMembership;
    onSubmitUpdate: (params: Partial<IMembership>) => Promise<void>;
    onAcceptTerminate: (membershipId: number) => Promise<void>;
    onAcceptReactivate: (membershipId: number) => Promise<void>;
}
export const MembershipInfoColumn: FC<IProps> = ({
    membership,
    onSubmitUpdate,
    onAcceptTerminate,
    onAcceptReactivate,
}) => {
    const { fetchTac, fetchAlternativeRecipient, isFetchingAlternativeRecipient } = useContext(MembershipsContext);
    const { hasPermission, hasPermissions } = usePermission();
    const { localize } = useContext(LanguageContext);
    const { openDrawer, closeDrawers } = useContext(ModalContext);
    const [alternativeRecipient, setAlternativeRecipient] = useState<IAlternativeInvoiceRecipient | undefined>(
        undefined
    );

    useEffect(() => {
        if (alternativeRecipient || !hasPermission("view_alternativeinvoicerecipient")) {
            return;
        }

        (async () => {
            if (!membership.alternative_invoice_recipient) {
                return;
            }
            const recipient = await fetchAlternativeRecipient(membership.alternative_invoice_recipient);
            setAlternativeRecipient(recipient);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [membership.alternative_invoice_recipient]);

    const showArena = "is_community_user";

    const hasPermissionToInactivate =
        hasPermission("manage_contract") &&
        (membership.is_possible_to_inactivate || dateIsPastOrToday(parseISO(membership.end_date ?? "")) || false);

    const onClickTac = useCallback(async (): Promise<void> => {
        const tacDocuments = await fetchTac({ membership_id: membership.id });
        if (tacDocuments) {
            tacDocuments.forEach((tac) => {
                if (tac.file.length > 0) {
                    window.open(tac.file);
                }
            });
        }
    }, [fetchTac, membership.id]);

    const onSelectRecipient = useCallback(
        async (recipient: IAlternativeInvoiceRecipient) => {
            await onSubmitUpdate({ alternative_invoice_recipient: recipient.uuid });
            closeDrawers();
        },
        [onSubmitUpdate, closeDrawers]
    );

    const recipientButton = useMemo(() => {
        if (!hasPermission("view_alternativeinvoicerecipient") || !!membership.end_date) {
            return undefined;
        }

        if (alternativeRecipient) {
            return {
                tag: "show",
                show: true,
                onClick: () =>
                    openDrawer(
                        ExistingRecipientDrawer,
                        { recipient: alternativeRecipient },
                        "alternativeInvoiceRecipient"
                    ),
            } as IButtonProps;
        }
        return {
            tag: "edit",
            show:
                !isFetchingAlternativeRecipient &&
                hasPermissions(["change_membership", "change_alternative_invoice_recipient"]),
            onClick: () =>
                openDrawer(
                    SetAlternativeRecipientDrawer,
                    {
                        onSelect: onSelectRecipient,
                    },
                    "alternativeInvoiceRecipient"
                ),
        } as IButtonProps;
    }, [
        membership.end_date,
        alternativeRecipient,
        hasPermission,
        hasPermissions,
        isFetchingAlternativeRecipient,
        onSelectRecipient,
        openDrawer,
    ]);

    const statusMembershipButton = useMemo(() => {
        return {
            tag: "edit",
            show: hasPermissionToInactivate,
            onClick: () =>
                openDrawer(
                    EditStatusMembershipDrawer,
                    {
                        membership: membership,
                        onAcceptTerminate: onAcceptTerminate,
                        onAcceptReactivate: onAcceptReactivate,
                    },
                    "statusMembership"
                ),
        } as IButtonProps;
    }, [hasPermissionToInactivate, membership, onAcceptReactivate, onAcceptTerminate, openDrawer]);

    const alternativeRecipientText = useMemo(() => {
        if (!membership.alternative_invoice_recipient) {
            return "-";
        }

        if (alternativeRecipient) {
            return `${alternativeRecipient.organization.name} (${prettifyOrgNumber(
                alternativeRecipient.organization.org_nr
            )})`;
        }

        return localize("assigned");
    }, [membership.alternative_invoice_recipient, alternativeRecipient, localize]);

    return (
        <>
            <List>
                <OrganizationNameField onSubmitUpdate={onSubmitUpdate} membership={membership} />
                <ListItem divider>
                    <ListItemText
                        primary={<LocalizeText tag="legalCompanyName" />}
                        secondary={membership?.organization.company_name}
                    />
                </ListItem>
                <CountryField membership={membership} onSubmitUpdate={onSubmitUpdate} />
                <ListItem divider>
                    <ListItemText
                        primary={<LocalizeText tag="orgNumber" />}
                        secondary={
                            membership?.organization.org_nr ? prettifyOrgNumber(membership?.organization.org_nr) : "-"
                        }
                    />
                </ListItem>
                <VatNumberField onSubmitUpdate={onSubmitUpdate} membership={membership} />
                {membership.alternative_invoice_recipient ? (
                    <InvoicingInfo data={membership} strikeThrough={true} />
                ) : (
                    <>
                        <InvoiceAddressFields membership={membership} onSubmitUpdate={onSubmitUpdate} />
                        <InvoiceMethodFields membership={membership} onSubmitUpdate={onSubmitUpdate} />
                        <EditableTextField
                            obj={membership}
                            tag="invoiceReference"
                            prop="user_invoice_reference"
                            onSubmitUpdate={onSubmitUpdate}
                            canEdit={hasPermission("change_membership") && !membership.end_date}
                        />
                    </>
                )}
                <PoNumberField onSubmitUpdate={onSubmitUpdate} membership={membership} />
                <ListItemWithButton
                    primary={<LocalizeText tag="alternativeInvoiceRecipient" />}
                    secondary={alternativeRecipientText}
                    button={recipientButton}
                />
                <ListItem divider>
                    <ListItemText primary={<LocalizeText tag="customerNumber" />} secondary={membership.agreement_nr} />
                </ListItem>

                <ReferencePersonFields membership={membership} onSubmitUpdate={onSubmitUpdate} />

                <ListItem divider>
                    <ListItemText
                        primary={<LocalizeText tag="branchCode" />}
                        secondary={membership.branch_code || "-"}
                    />
                </ListItem>
                <ListItem divider>
                    <ListItemText
                        primary={<LocalizeText tag="from" />}
                        secondary={
                            membership.start_date
                                ? format(parseISO(membership.start_date ?? ""), dateFormats.WEBDATE)
                                : ""
                        }
                    />
                </ListItem>
                <ListItemWithButton
                    primary={<LocalizeText tag="statusMembership" />}
                    secondary={getActiveStatus(membership.end_date)}
                    button={statusMembershipButton}
                />

                <ListItem divider>
                    <ListItemText
                        primary={<LocalizeText tag="contactPersons" />}
                        secondary={
                            <span style={{ display: "flex", flexDirection: "column" }}>
                                <span>
                                    <LocalizeText tag="activePlural" />: {membership.contact_persons_count}
                                </span>
                                <span>
                                    <LocalizeText tag="invitations" />: {membership.invitations_count}
                                </span>
                            </span>
                        }
                    />
                </ListItem>
                <ListItem divider>
                    <ListItemText
                        primary={<LocalizeText tag="subscriptions" />}
                        secondary={membership.subscriptions_count}
                    />
                </ListItem>
                <HasPermission requiredPerm={showArena}>
                    <FreeFromTextField membership={membership} onSubmitUpdate={onSubmitUpdate} />
                </HasPermission>
                <DocumentsField membership={membership} />
            </List>
            <div className="BottomRow">
                <Link
                    sx={{
                        fontSize: "1rem",
                        marginLeft: "auto",
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onClickTac}
                    underline="hover"
                >
                    <LocalizeText tag="tac" />
                    <OpenInNewIcon sx={{ marginLeft: "5px", fontSize: "1rem" }} />
                </Link>
            </div>
        </>
    );
};
